// src/pages/Login.jsx
import React, { useState } from 'react';
import { Form, Input, Button, Spin, Card, Typography, Alert } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons'; 
import { useAuth } from '../contexts/AuthContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

/**
 * Login Component
 * 
 * Handles user authentication using Firebase Auth.
 * Provides a clean login interface with form validation and error handling.
 * Automatically redirects authenticated users to their appropriate dashboard.
 */
export default function Login() {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  
  // Local state for form submission and error handling
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /**
   * Handles form submission for user login
   * @param {Object} values - Form values containing email and password
   */
  const onFinish = async ({ email, password }) => {
    setSubmitting(true);
    setErrorMsg(''); // Clear any previous errors
    
    try {
      // Attempt to sign in with Firebase Auth
      await signInWithEmailAndPassword(auth, email, password);
      // Redirect to home page after successful login
      navigate('/');
    } catch (err) {
      // Display error message to user
      setErrorMsg(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading spinner while checking authentication status
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Redirect authenticated users to home page
  if (currentUser) {
    navigate('/');
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f5f5f5'
      }}
    >
      {/* Login Card */}
      <Card style={{ width: 360, textAlign: 'center', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
        <Title level={3} style={{ marginBottom: 24 }}>Login</Title>
        
        {/* Error Alert */}
        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {/* Login Form */}
        <Form
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          {/* Email Input */}
          <Form.Item 
            name="email" 
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          > 
            <Input 
              placeholder="Email" 
              prefix={<UserOutlined />} 
            />
          </Form.Item>
          
          {/* Password Input */}
          <Form.Item 
            name="password" 
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          > 
            <Input.Password 
              placeholder="Password" 
              prefix={<LockOutlined />} 
            />
          </Form.Item>
          
          {/* Submit Button */}
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={submitting} 
              block
              size="large"
            >
              Log In
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}